import Image from 'next/image';
import type { Dispatch, SetStateAction } from 'react';
import type { Entity } from '@strapi/strapi';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnyPage, GlobalData, IconSvgObject, Image as NextImage } from '@tmh-website/shared/cms';
import type { IconId } from '@tmh-website/components/icons/phosphor-extension';
import ButtonIcon from '@tmh-website/ui/ButtonIcon';
import CMSLink from '@tmh-website/components/shared/CMSLink';
import CollapseContainer from '@tmh-website/components/shared/CollapseContainer';
import Icon from '@tmh-website/components/icons/phosphor-extension/Icon';
import IconStandalone from '@tmh-website/components/icons/phosphor-extension/IconStandalone';
import X from '@tmh-website/components/icons/phosphor-extension/icons/X';
import { Audience, type LocalePrefix, Project } from '@tmh-website/shared/const';
import { Color } from '@tmh-website/shared/colors';
import { filterNavigationByAudience } from '@tmh-website/services/cms.service';
import { GlobalContext, ProjectContext, useIsMobile } from '@tmh-website/shared';
import { hasAudiences, hasLocales } from '@tmh-website/shared/const';
import { isNavigationPageLinkGroup, isValidLinkField } from '@tmh-website/shared/cms';
import { toggleTrustBadge } from '@tmh-website/services/floating-ui.service';
import { useLogoComponent } from '@tmh-website/shared/hooks';

import HeaderButtons from '../HeaderButtons';
import LocaleSelect from '../../LocaleSelect';

import * as Styled from './Menu.styles';
import CategoryLink from './CategoryLink';
import MenuLink from './MenuLink';

export interface MenuProps {
  menuIsVisible: boolean;
  setMenuIsVisible: Dispatch<SetStateAction<boolean>>;
  locale: LocalePrefix;
  currentPath: string;
  page?: AnyPage;

  /**
   * Enable this flag when this component is used outside of the Next.js context.
   */
  isStandaloneMode: boolean;
}

interface ImageEntry {
  id: string;
  image: NextImage;
}

const Menu = ({ menuIsVisible, setMenuIsVisible, locale, currentPath, page, isStandaloneMode }: MenuProps) => {
  const { navigation, audience, audienceIsLoaded, customLogo, menuQuickLinks } = useContext(GlobalContext);
  const { project } = useContext(ProjectContext);

  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [activeCategoryIdHistory, setActiveCategoryIdHistory] = useState<number[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<Entity.ID | null>();
  const [imagesInDom, setImagesInDom] = useState<ImageEntry[]>([]);
  const [activeImage, setActiveImage] = useState<string>('');

  const isMobile = useIsMobile();
  const LogoComponent = useLogoComponent();
  const { t } = useTranslation();

  const envHasLocale = hasLocales(project);
  const envHasAudiences = hasAudiences(locale, project);
  const activeCategory = navigation?.find(element => element.id === activeCategoryId);

  // Add all top level images to the DOM when the menu is opened for the first time
  useEffect(() => {
    if (menuIsVisible && !hasBeenOpened) {
      const topLevelImages: ImageEntry[] = [...imagesInDom];
      (navigation ?? []).forEach(category => {
        if (isNavigationPageLinkGroup(category)) {
          if (category.link.image) {
            topLevelImages.push({ id: category.id?.toString(), image: category.link.image });
          }
        } else {
          if (category.image) {
            topLevelImages.push({ id: category.id?.toString(), image: category.image });
          }
        }
      });
      setImagesInDom(topLevelImages);
      setHasBeenOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, navigation, menuIsVisible, hasBeenOpened]);

  // Add all category images to the DOM when the category is active for the first time
  useEffect(() => {
    const categoryimages: ImageEntry[] = [...imagesInDom];
    if (activeCategory) {
      if (isNavigationPageLinkGroup(activeCategory) && !activeCategoryIdHistory.includes(activeCategory.id as number)) {
        (activeCategory.children ?? []).forEach(child => {
          if (child.image) {
            categoryimages.push({ id: activeCategory.id + '_' + child.id, image: child.image });
          }
        });
        setActiveCategoryIdHistory([activeCategory.id as number, ...activeCategoryIdHistory]);
      }
    }
    setImagesInDom(categoryimages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, navigation, activeCategory]);

  useEffect(() => {
    toggleTrustBadge(!menuIsVisible);
    if (menuIsVisible) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }
  }, [menuIsVisible]);

  let quickLinks: GlobalData['menuQuickLinks'] = [];
  if (project === Project.Website && audienceIsLoaded && !!audience) {
    quickLinks = (menuQuickLinks ?? []).filter(quickLink => {
      if (isValidLinkField(quickLink.link)) {
        return quickLink.link.relevantAudiences === undefined || quickLink.link.relevantAudiences.includes(audience);
      }
    });
  }

  if (project === Project.Styleguide) {
    quickLinks = menuQuickLinks ?? [];
  }

  const hasCustomLogo: boolean = typeof customLogo !== 'undefined';

  const logoWrapperMaxWidth: Styled.LogoWrapperProps['$maxWidth'] = project === Project.Energy ? '200px' : undefined;

  return (
    <Styled.Menu $isOpened={menuIsVisible} data-cy='menu'>
      <Styled.GlobalCol>
        <Styled.Toolbar>
          <Styled.ToolbarButtonsContainer>
            <HeaderButtons
              isStandaloneMode={isStandaloneMode}
              buttonsThemeColor={Color.Blue_1000_Deep}
              dropdownsThemeColor={Color.Neutral_200_OffWhite}
              locale={locale}
              inMenu
              audience={audience}
              audienceIsLoaded
              outlineButtonThemeColor={Color.Neutral_0_White}
            />
            <ButtonIcon
              themeColor={Color.Blue_1000_Deep}
              isTransparent
              isSmall={true}
              aria-label={t('global:menu.closeMenu') as string}
              data-cy='closeMenuButton'
              onClick={() => setMenuIsVisible(!menuIsVisible)}>
              <X size={40} isSmall />
            </ButtonIcon>
          </Styled.ToolbarButtonsContainer>
        </Styled.Toolbar>
        {envHasAudiences && (
          <Styled.AdjustedAudienceTabs
            locale={locale}
            currentPath={currentPath}
            headerThemeColor={Color.Blue_1000_Deep}
            isStandaloneMode={!!isStandaloneMode}
            showButton={false}
            inMenu
          />
        )}
        <Styled.GlobalNav aria-label={t('global:navigation.menu.global') as string} data-track-global-nav=''>
          {(
            (envHasAudiences
              ? filterNavigationByAudience(navigation ?? [], audience ?? Audience.Default)
              : navigation) ?? []
          ).map(category => {
            const isOpen = activeCategory?.id === category.id && isNavigationPageLinkGroup(activeCategory);
            const hasChildren = isNavigationPageLinkGroup(category);
            const link = hasChildren ? category.link : category;
            function stateChangeHandler(setToOpen: boolean) {
              setActiveCategoryId(setToOpen ? category.id : null);
              setActiveImage(category.id.toString());
            }

            return (
              <Fragment key={category.id}>
                <CategoryLink
                  element={category}
                  isOpen={isOpen}
                  onStateChange={stateChangeHandler}
                  onClick={() => {
                    if (!hasChildren && isValidLinkField(link.page)) {
                      setMenuIsVisible(false);
                    } else if (hasChildren && isMobile) {
                      stateChangeHandler(!isOpen);
                    }
                  }}
                  isStandaloneMode={isStandaloneMode}
                  locale={locale}
                  navigation={navigation !== undefined ? navigation : []}
                />
                {hasChildren && (
                  <CollapseContainer isOpen={isMobile === false || isOpen}>
                    <Styled.CategoryCol
                      $isVisible={isOpen}
                      aria-label={
                        t('global:navigation.menu.category', {
                          page: isValidLinkField(category.link.page) ? category.link.page.label : 'page',
                        }) as string
                      }>
                      {(category.children ?? []).map(child => {
                        if (isValidLinkField(child.page)) {
                          return (
                            <CMSLink
                              key={child.id}
                              link={child.page}
                              isStandaloneMode={isStandaloneMode}
                              locale={locale}>
                              <MenuLink
                                label={child.page.label}
                                isSelected={page?.path === child.page.value}
                                tabIndex={isOpen ? undefined : -1}
                                onStateChange={() => setActiveImage(`${category.id}_${child.id}`)}
                                onClick={() => setMenuIsVisible(false)}
                              />
                            </CMSLink>
                          );
                        }
                      })}
                    </Styled.CategoryCol>
                  </CollapseContainer>
                )}
              </Fragment>
            );
          })}
          {quickLinks.length > 0 && (
            <Styled.QuickLinksContainer aria-label={t('global:navigation.menu.quickLinks') as string}>
              {quickLinks.map(quickLink => {
                if (isValidLinkField(quickLink.link)) {
                  return (
                    <CMSLink
                      key={quickLink.link.value}
                      link={quickLink.link}
                      isStandaloneMode={isStandaloneMode}
                      locale={locale}>
                      <Styled.QuickLink onClick={() => setMenuIsVisible(false)}>
                        {quickLink.icon &&
                          (isStandaloneMode ? (
                            <IconStandalone iconId={quickLink.icon as IconId} size={24} />
                          ) : (
                            <Icon icon={quickLink.icon as IconSvgObject} />
                          ))}
                        <span>{quickLink.link.label}</span>
                      </Styled.QuickLink>
                    </CMSLink>
                  );
                } else {
                  return null;
                }
              })}
            </Styled.QuickLinksContainer>
          )}
        </Styled.GlobalNav>
        <Styled.BottomContainer>
          <Styled.BottomButtonsContainer>
            {envHasLocale && (locale as string) !== 'default' && (
              <LocaleSelect locale={locale} themeColor={Color.Blue_1000_Deep} />
            )}
          </Styled.BottomButtonsContainer>

          {!hasCustomLogo && (
            <Styled.LogoWrapper $maxWidth={logoWrapperMaxWidth}>
              <LogoComponent />
            </Styled.LogoWrapper>
          )}
        </Styled.BottomContainer>
        <Styled.BackgroundPlane />
      </Styled.GlobalCol>
      <Styled.ImageCol onClick={() => setActiveCategoryId(null)}>
        {imagesInDom.map(imageEntry => {
          if ((!isMobile || isMobile === undefined) && imageEntry.image) {
            return (
              <Styled.MenuImage
                as={isStandaloneMode ? 'img' : Image}
                key={imageEntry.id}
                $isVisible={activeImage === imageEntry.id}
                src={imageEntry.image.url}
                alt={imageEntry.image.alternativeText}
                fill
                style={{ objectFit: 'cover' }}
                sizes='50vw'
              />
            );
          }
        })}
      </Styled.ImageCol>
    </Styled.Menu>
  );
};

export default Menu;
